import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { EmptyState } from '../../components/ui/empty-state';
import { InfoTooltip } from '../../components/ui/info-tooltip';
import { ExpandableRow, DetailPanel } from '../../components/ui/expandable-row';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  AlertTriangle,
  Plus,
  Send,
  Trash2,
  Edit,
  Loader2,
  Search,
  Building,
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  Filter,
  ListChecks
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminThreats = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [threats, setThreats] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPushModal, setShowPushModal] = useState(false);
  const [selectedThreat, setSelectedThreat] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, threat: null });
  const [filterSeverity, setFilterSeverity] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    severity: 'medium',
    category: 'external',
    recommended_actions: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [threatsRes, orgsRes] = await Promise.all([
        api.get('/admin/threats'),
        api.get('/admin/organizations')
      ]);
      setThreats(threatsRes.data.threats || []);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      toast.error('Data laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Vul naam en beschrijving in');
      return;
    }
    
    setProcessing(true);
    try {
      const payload = {
        ...formData,
        recommended_actions: formData.recommended_actions 
          ? formData.recommended_actions.split('\n').filter(a => a.trim())
          : []
      };
      
      await api.post('/admin/threats', payload);
      toast.success('Bedreiging aangemaakt');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        severity: 'medium',
        category: 'external',
        recommended_actions: ''
      });
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Aanmaken mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const handlePush = async () => {
    if (!selectedThreat) return;
    
    setProcessing(true);
    try {
      const payload = selectedOrgs.length > 0 
        ? { organization_ids: selectedOrgs }
        : {};
      
      const response = await api.post(`/admin/threats/${selectedThreat.id}/push`, payload);
      toast.success(response.data.message);
      setShowPushModal(false);
      setSelectedThreat(null);
      setSelectedOrgs([]);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Push mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.threat) return;
    
    setProcessing(true);
    try {
      await api.delete(`/admin/threats/${deleteDialog.threat.id}`);
      toast.success('Bedreiging verwijderd');
      setDeleteDialog({ open: false, threat: null });
      fetchData();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteClick = (threat) => {
    setDeleteDialog({ open: true, threat });
  };

  const openPushModal = (threat) => {
    setSelectedThreat(threat);
    setSelectedOrgs([]);
    setShowPushModal(true);
  };

  const toggleOrgSelection = (orgId) => {
    setSelectedOrgs(prev => 
      prev.includes(orgId) 
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    const labels = {
      critical: 'Kritiek',
      high: 'Hoog',
      medium: 'Medium',
      low: 'Laag'
    };
    return (
      <Badge className={colors[severity] || colors.medium}>
        {labels[severity] || severity}
      </Badge>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      external: 'Extern',
      internal: 'Intern',
      natural: 'Natuurlijk'
    };
    return labels[category] || category;
  };

  const filteredThreats = threats.filter(t => {
    const matchesSearch = !searchQuery || 
      t.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeverity = filterSeverity === 'all' || t.severity === filterSeverity;
    return matchesSearch && matchesSeverity;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Dreigingsbeeld
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Beheer en push bedreigingen naar klantorganisaties
            </p>
          </div>
          <Button
            className="bg-black text-white hover:bg-neutral-800"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-threat-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nieuwe Bedreiging
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-neutral-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{threats.length}</p>
                  <p className="text-xs text-neutral-500">Totaal</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-red-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">
                    {threats.filter(t => t.severity === 'critical' || t.severity === 'high').length}
                  </p>
                  <p className="text-xs text-neutral-500">Hoog/Kritiek</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">
                    {threats.filter(t => (t.pushed_to?.length || 0) > 0).length}
                  </p>
                  <p className="text-xs text-neutral-500">Gepusht</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-neutral-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Building className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{organizations.length}</p>
                  <p className="text-xs text-neutral-500">Organisaties</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              placeholder="Zoek bedreiging..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="threat-search"
            />
          </div>
          <Select value={filterSeverity} onValueChange={setFilterSeverity}>
            <SelectTrigger className="w-full sm:w-[180px] border-neutral-200">
              <Filter className="h-4 w-4 mr-2 text-neutral-400" strokeWidth={1.5} />
              <SelectValue placeholder="Filter ernst" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle ernst</SelectItem>
              <SelectItem value="critical">Kritiek</SelectItem>
              <SelectItem value="high">Hoog</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Laag</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Threats Table */}
        <Card className="border-neutral-200">
          <CardContent className="p-0">
            {filteredThreats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="threats-table">
                  <thead className="sticky top-0 z-10 bg-white dark:bg-neutral-900">
                    <tr className="border-b border-neutral-200 dark:border-neutral-700">
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Bedreiging</th>
                      <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Ernst</th>
                      <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Categorie</th>
                      <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Gepusht naar</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Aangemaakt</th>
                      <th className="text-right text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Acties</th>
                      <th className="w-10 bg-neutral-50 dark:bg-neutral-800"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredThreats.map((threat, index) => (
                      <ExpandableRow 
                        key={threat.id} 
                        className={`border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 ${
                          index % 2 === 1 ? 'bg-neutral-50/50 dark:bg-neutral-800/20' : ''
                        }`}
                        data-testid={`threat-row-${threat.id}`}
                        expandedContent={
                          <div className="space-y-4">
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                                Beschrijving
                              </p>
                              <p className="text-sm text-neutral-700 dark:text-neutral-300">
                                {threat.description}
                              </p>
                            </div>
                            
                            {threat.recommended_actions?.length > 0 && (
                              <div className="space-y-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center gap-1">
                                  <ListChecks className="h-3 w-3" />
                                  Aanbevolen acties
                                </p>
                                <ul className="space-y-1">
                                  {threat.recommended_actions.map((action, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                                      <span className="text-neutral-400">•</span>
                                      {action}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {threat.pushed_to?.length > 0 && (
                              <div className="space-y-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                                  Gepusht naar organisaties
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {organizations
                                    .filter(org => threat.pushed_to.includes(org.id))
                                    .map(org => (
                                      <Badge key={org.id} variant="secondary" className="text-xs">
                                        {org.name}
                                      </Badge>
                                    ))
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        }
                      >
                        <td className="p-4">
                          <div className="max-w-md">
                            <p className="text-sm font-medium text-black">{threat.name}</p>
                            <p className="text-xs text-neutral-500 truncate mt-0.5">
                              {threat.description?.substring(0, 60)}...
                            </p>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {getSeverityBadge(threat.severity)}
                        </td>
                        <td className="p-4 text-center">
                          <span className="text-sm text-neutral-600">
                            {getCategoryLabel(threat.category)}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={threat.pushed_to?.length > 0 
                            ? "bg-green-50 text-green-700" 
                            : "bg-neutral-100 text-neutral-500"
                          }>
                            {threat.pushed_to?.length || 0} / {organizations.length}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm text-neutral-500">
                            <Clock className="h-4 w-4" strokeWidth={1.5} />
                            {new Date(threat.created_at).toLocaleDateString('nl-NL')}
                          </div>
                        </td>
                        <td className="p-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              className="bg-blue-600 text-white hover:bg-blue-700"
                              onClick={() => openPushModal(threat)}
                              data-testid={`push-threat-${threat.id}`}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Push
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteClick(threat)}
                              data-testid={`delete-threat-${threat.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </ExpandableRow>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={AlertTriangle}
                title={searchQuery || filterSeverity !== 'all' ? 'Geen resultaten' : 'Geen bedreigingen'}
                description={
                  searchQuery || filterSeverity !== 'all'
                    ? 'Probeer een andere zoekopdracht of filter'
                    : 'Maak een nieuwe bedreiging aan om te beginnen met threat intelligence'
                }
                actionLabel={searchQuery || filterSeverity !== 'all' ? 'Filters wissen' : 'Nieuwe Bedreiging'}
                onAction={searchQuery || filterSeverity !== 'all' 
                  ? () => { setSearchQuery(''); setFilterSeverity('all'); }
                  : () => setShowCreateModal(true)
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Threat Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe Bedreiging</DialogTitle>
            <DialogDescription>
              Maak een nieuwe bedreiging aan die je kunt pushen naar klantorganisaties
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Naam *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="bijv. Phishing aanval gericht op financiële sector"
                data-testid="threat-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Beschrijving *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Gedetailleerde beschrijving van de bedreiging..."
                rows={3}
                data-testid="threat-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ernst</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(value) => setFormData({...formData, severity: value})}
                >
                  <SelectTrigger data-testid="threat-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Laag</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">Hoog</SelectItem>
                    <SelectItem value="critical">Kritiek</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Categorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({...formData, category: value})}
                >
                  <SelectTrigger data-testid="threat-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Extern</SelectItem>
                    <SelectItem value="internal">Intern</SelectItem>
                    <SelectItem value="natural">Natuurlijk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Aanbevolen acties (één per regel)</Label>
              <Textarea
                value={formData.recommended_actions}
                onChange={(e) => setFormData({...formData, recommended_actions: e.target.value})}
                placeholder="Activeer MFA voor alle gebruikers&#10;Train medewerkers op phishing herkenning&#10;..."
                rows={3}
                data-testid="threat-actions-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleCreate}
              disabled={processing}
              data-testid="create-threat-submit"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Aanmaken
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Push Threat Modal */}
      <Dialog open={showPushModal} onOpenChange={setShowPushModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Bedreiging Pushen</DialogTitle>
            <DialogDescription>
              Push "{selectedThreat?.name}" naar geselecteerde organisaties
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Selecteer geen organisaties om naar <strong>alle</strong> organisaties te pushen
              </p>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {organizations.map((org) => {
                const alreadyPushed = selectedThreat?.pushed_to?.includes(org.id);
                return (
                  <div 
                    key={org.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      alreadyPushed 
                        ? 'bg-green-50 border-green-200 cursor-not-allowed' 
                        : selectedOrgs.includes(org.id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-neutral-200 hover:bg-neutral-50'
                    }`}
                    onClick={() => !alreadyPushed && toggleOrgSelection(org.id)}
                    data-testid={`org-select-${org.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                        alreadyPushed ? 'bg-green-100' : 'bg-neutral-100'
                      }`}>
                        <Building className={`h-4 w-4 ${
                          alreadyPushed ? 'text-green-600' : 'text-neutral-600'
                        }`} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-black">{org.name}</p>
                        <p className="text-xs text-neutral-500">CWI: {org.cwi_score}</p>
                      </div>
                    </div>
                    {alreadyPushed ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Gepusht
                      </Badge>
                    ) : selectedOrgs.includes(org.id) ? (
                      <CheckCircle2 className="h-5 w-5 text-blue-600" />
                    ) : null}
                  </div>
                );
              })}
            </div>
            
            <p className="text-sm text-neutral-500 mt-4">
              {selectedOrgs.length > 0 
                ? `${selectedOrgs.length} organisatie(s) geselecteerd`
                : 'Geen selectie = alle organisaties'}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPushModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-blue-600 text-white hover:bg-blue-700"
              onClick={handlePush}
              disabled={processing}
              data-testid="push-threat-submit"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Push naar {selectedOrgs.length > 0 ? selectedOrgs.length : 'alle'} organisatie(s)
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
        title="Bedreiging verwijderen"
        description={`Weet u zeker dat u "${deleteDialog.threat?.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`}
        confirmLabel="Ja, verwijderen"
        cancelLabel="Annuleren"
        variant="danger"
        loading={processing}
        onConfirm={handleDelete}
      />
    </AdminLayout>
  );
};

export default AdminThreats;
